import React, { useState, useEffect } from 'react';
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { Edit3, Ticket, Trash2, Calendar, Users, Percent, DollarSign, Power } from 'lucide-react';
import { Link } from 'react-router-dom';

const AllCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  const adminToken = localStorage.getItem('adminToken');

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getAllCoupons`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (response.data.success) {
        setCoupons(response.data.coupons);
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch coupons' });
    } finally {
      setTimeout(() => setLoading(false), 600);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [adminToken]);

  // --- Logic: Delete ---
  const handleDelete = async (id, code) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete coupon ${code}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        // Replace with your actual delete endpoint
        await axios.delete(`${baseurl}deleteCoupon/${id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        Swal.fire('Deleted!', 'Coupon has been removed.', 'success');
        fetchCoupons();
      } catch (err) {
        Swal.fire('Error', 'Failed to delete coupon.', 'error');
      }
    }
  };

  // --- Logic: Sorting ---
  const sortedData = [...coupons].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // --- Logic: Filtering ---
  const filteredData = sortedData.filter(c => 
    `${c.code} ${c.description}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-success',
      expired: 'bg-danger',
      exhausted: 'bg-warning text-dark'
    };
    return <span className={`badge ${colors[status] || 'bg-secondary'} text-uppercase`}>{status}</span>;
  };

  const SkeletonRow = () => (
    <tr className="skeleton-row">
      <td colSpan="7"><div className="skeleton-line"></div></td>
    </tr>
  );

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex', minHeight: '100vh' }}>
        <div className="p-4" style={{ flex: 1, overflow: 'auto' }}>
          
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#1B2559' }}>Subscription Coupons</h2>
              <p className="text-muted m-0">Create and manage discount codes for your subscription plans.</p>
            </div>
            <Link to="/create-coupon" className="btn btn-primary rounded-pill px-4 shadow-sm fw-bold">
              + Create New Coupon
            </Link>
          </div>

          {/* CONTROL BOX */}
          <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '15px' }}>
            <div className="row g-3 align-items-center">
              <div className="col-md-4">
                <div className="input-group border rounded-3 bg-light m-0">
                  <span className="input-group-text bg-transparent border-0"><Ticket size={18} className="text-muted"/></span>
                  <input 
                    type="text" 
                    className="form-control bg-transparent border-0 shadow-none" 
                    placeholder="Search by code or description..." 
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4 border-0 text-muted small fw-bold">COUPON CODE</th>
                    <th className="border-0 text-muted small fw-bold">DISCOUNT</th>
                    <th className="border-0 text-muted small fw-bold text-center">USAGE (USED/MAX)</th>
                    <th className="border-0 text-muted small fw-bold text-center">STATUS</th>
                    <th className="border-0 text-muted small fw-bold text-center">DURATION</th>
                    <th className="border-0 text-muted small fw-bold">EXPIRY</th>
                    <th className="border-0 text-muted small fw-bold text-center">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [1,2,3].map(i => <SkeletonRow key={i} />)
                  ) : filteredData.length > 0 ? (
                    filteredData.map((coupon) => (
                      <tr key={coupon.id}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center">
                            <div className="avatar-icon me-3"><Ticket size={18} /></div>
                            <div>
                              <div className="fw-bold text-primary">{coupon.code}</div>
                              <div className="extra-small text-muted text-truncate" style={{maxWidth: '180px'}}>{coupon.description}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="fw-bold text-dark d-flex align-items-center">
                            {coupon.discount_type === 'percentage' ? <Percent size={14} className="me-1"/> : <DollarSign size={14} className="me-1"/>}
                            {coupon.discount_value} {coupon.discount_type === 'percentage' ? '%' : 'USD'}
                          </div>
                          <div className="extra-small text-muted">Plan: {coupon.apply_to_type || 'All'}</div>
                        </td>
                        <td className="text-center">
                          <div className="d-flex flex-column align-items-center">
                            <div className="small fw-bold text-dark"><Users size={12} className="me-1"/> {coupon.used_count} / {coupon.usage_limit || '∞'}</div>
                            <div className="progress w-75 mt-1" style={{height: '4px'}}>
                                <div 
                                    className="progress-bar bg-primary" 
                                    style={{width: `${coupon.usage_limit ? (coupon.used_count / coupon.usage_limit) * 100 : 0}%`}}
                                ></div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          {getStatusBadge(coupon.calculated_status)}
                        </td>
                        <td className="text-center">
                            <div className="small text-dark fw-bold">
                                <Power size={12} className="me-1 text-primary"/>
                                {coupon.recurring_limit === 99 ? 'Lifetime' : `${coupon.recurring_limit} Cycle(s)`}
                            </div>
                        </td>
                        <td className="text-muted small">
                            <div className="d-flex align-items-center">
                                <Calendar size={12} className="me-1"/> {formatDate(coupon.end_date)}
                            </div>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Link to={`/create-coupon?id=${coupon.id}&action=edit`} className="btn btn-light btn-sm rounded-circle p-2 text-primary shadow-sm">
                              <Edit3 size={16} />
                            </Link>
                            <button 
                                onClick={() => handleDelete(coupon.id, coupon.code)}
                                className="btn btn-light btn-sm rounded-circle p-2 text-danger shadow-sm"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="7" className="text-center py-5 text-muted">No coupons found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .avatar-icon {
          width: 38px;
          height: 38px;
          background: #F4F7FE;
          color: #4318FF;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
        }
        .extra-small { font-size: 11px; }
        .bg-soft-primary { background: #E9EDF7; }
        .skeleton-line {
          height: 20px;
          width: 90%;
          margin: 15px auto;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default AllCoupons;